import type { ImagePickerAsset } from 'expo-image-picker';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { UseFieldArrayAppend, UseFieldArrayUpdate } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { Menu } from 'react-native-paper';

import { CameraIcon, PhotoIcon } from '@/assets';
import { useUpdateOrganizationPending } from '@/features/api/hooks/grapevine-organization/useUpdateOrganization';
import { useUploadTemporaryMediaItem } from '@/features/api/hooks/shared-media/useUploadTemporaryMediaItem';
import type { ImagePickType } from '@/features/media/types/picker';
import { pickImages } from '@/features/media/utils/picker';
import { AddImageButton } from '@/features/profile-setting/components/organization-edit/AddImageButton';
import { useDetailImageUpload } from '@/features/profile-setting/components/organization-edit/DetailImageUploadContext';
import type {
  DetailImageEntry,
  DetailImageField,
  OrganizationEditFormValues,
  PickActionOption
} from '@/features/profile-setting/components/organization-edit/types';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { ExtendedMenu } from '@/shared/components/ExtendedMenu';
import { isNative } from '@/shared/utils/platform';

const MAX_IMAGES = 8;

type Props = {
  /** Field-array state + actions owned by the parent DetailImageList — a
   * field array must have a single useFieldArray instance, so they are
   * passed down rather than re-created here. */
  fields: DetailImageField[];
  append: UseFieldArrayAppend<OrganizationEditFormValues, 'detailImages'>;
  update: UseFieldArrayUpdate<OrganizationEditFormValues, 'detailImages'>;
};

export const DetailImageMenu: FC<Props> = ({ fields, append, update }) => {
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-edit'
  });
  const { setUploading, setError, clear } = useDetailImageUpload();
  const { getValues } = useFormContext<OrganizationEditFormValues>();

  const isPending = useUpdateOrganizationPending();
  const { mutateAsync: uploadTemporaryMediaItem } =
    useUploadTemporaryMediaItem();
  const [menuVisible, setMenuVisible] = useState(false);

  const canAddMore = !isPending && fields.length < MAX_IMAGES;

  const pickActions: PickActionOption[] = useMemo(() => {
    const actions: PickActionOption[] = [
      { type: 'select' as const, icon: PhotoIcon }
    ];
    if (isNative) {
      // NOTE: The camera is only available on native, so web has a single action.
      actions.push({ type: 'take' as const, icon: CameraIcon });
    }
    return actions;
  }, []);

  const toEntry = useCallback(
    (asset: ImagePickerAsset): DetailImageEntry => ({
      key: asset.uri,
      asset
    }),
    []
  );

  const handleAdd = useCallback(
    async (pick: ImagePickType) => {
      setMenuVisible(false);

      const remaining = MAX_IMAGES - fields.length;
      if (remaining <= 0) {
        return;
      }

      const assets = await pickImages(pick, {
        allowsMultipleSelection: pick === 'select',
        selectionLimit: remaining
      });
      if (!assets) {
        return;
      }

      // Cap to the remaining slots — selectionLimit is only a picker hint and
      // is not enforced on every platform.
      const entries = assets
        .slice(0, remaining)
        .map((element) => toEntry(element));
      append(entries);

      for (const entry of entries) {
        setUploading(entry.key);
        void uploadTemporaryMediaItem(entry.asset!)
          // oxlint-disable-next-line promise/always-return
          .then((item) => {
            clear(entry.key);
            const current = getValues('detailImages');
            const index = current.findIndex((f) => f.key === entry.key);
            if (index !== -1) {
              update(index, {
                ...current[index],
                temporaryMediaItemId: item.id
              });
            }
          })
          .catch(() => setError(entry.key));
      }
    },
    [
      append,
      fields.length,
      toEntry,
      setUploading,
      setError,
      clear,
      uploadTemporaryMediaItem,
      update,
      getValues
    ]
  );

  if (pickActions.length > 1) {
    return (
      <ExtendedMenu
        visible={menuVisible}
        onDismiss={() => setMenuVisible(false)}
        anchor={
          <AddImageButton
            label={t('actions.addImage')}
            onPress={() => setMenuVisible(true)}
            disabled={!canAddMore}
            style={styles.addButton}
          />
        }
      >
        {pickActions.map((action) => (
          <Menu.Item
            key={action.type}
            leadingIcon={({ color, size }) => (
              <action.icon fill={color} width={size} height={size} />
            )}
            onPress={() => handleAdd(action.type)}
            title={t('actions.addImage')}
          />
        ))}
      </ExtendedMenu>
    );
  }

  return (
    <AddImageButton
      label={t('actions.addImage')}
      onPress={() => void handleAdd(pickActions[0].type)}
      disabled={!canAddMore}
      style={styles.addButton}
    />
  );
};

const styles = StyleSheet.create({
  addButton: { alignSelf: 'flex-start' }
});
